export enum SuggestionFilterValues {
	ALL = 'ALL',
	CREATED_BY_ME = 'CREATED_BY_ME',
}

export enum SuggestionSortValues {
	/** Document order — the order the plugin already keeps `orderedSuggestionIds` in. */
	TOP_TO_BOTTOM = 'TOP_TO_BOTTOM',
	/** Most recently generated suggestion first. */
	NEW_TO_OLD = 'NEW_TO_OLD',
}

/**
 * The subset of `Suggestion` the panel filters and sorts on. Kept structural so the helpers can be
 * unit tested (and reused for analytics snapshots) without building a full suggestion.
 */
export type FilterableSuggestion = {
	createdAt?: string;
	createdByUser?: { accountId?: string } | null;
	id: string;
};

const getCreatedAtTime = (createdAt?: string): number => {
	const time = createdAt ? Date.parse(createdAt) : Number.NaN;
	// Suggestions without a usable timestamp sort last under "New → Old".
	return Number.isNaN(time) ? 0 : time;
};

export const filterSuggestions = <T extends FilterableSuggestion>(
	suggestions: readonly T[],
	currentFilter: SuggestionFilterValues,
	currentUserId?: string | null,
): T[] => {
	if (currentFilter !== SuggestionFilterValues.CREATED_BY_ME) {
		return [...suggestions];
	}

	// Without an account id nothing can be attributed to the current user, so nothing matches.
	return suggestions.filter(
		(suggestion) => !!currentUserId && suggestion.createdByUser?.accountId === currentUserId,
	);
};

export const sortSuggestions = <T extends FilterableSuggestion>(
	suggestions: readonly T[],
	currentSort: SuggestionSortValues,
): T[] => {
	if (currentSort !== SuggestionSortValues.NEW_TO_OLD) {
		// Already in document order.
		return [...suggestions];
	}

	// Decorate with the document index so suggestions sharing a timestamp keep document order.
	const decorated = suggestions.map((suggestion, index) => ({
		createdAtTime: getCreatedAtTime(suggestion.createdAt),
		index,
		suggestion,
	}));

	decorated.sort((a, b) => {
		const byCreatedAt = b.createdAtTime - a.createdAtTime;
		return byCreatedAt !== 0 ? byCreatedAt : a.index - b.index;
	});

	return decorated.map(({ suggestion }) => suggestion);
};

/**
 * Applies the panel's current filter and sort to the suggestions held in plugin state. Returns
 * `undefined` when plugin state is not available yet so callers can keep distinguishing
 * "no editor" from "no suggestions".
 */
export const getVisibleSuggestions = <T extends FilterableSuggestion>(
	suggestions: readonly T[] | undefined,
	{
		currentFilter,
		currentSort,
		currentUserId,
	}: {
		currentFilter: SuggestionFilterValues;
		currentSort: SuggestionSortValues;
		currentUserId?: string | null;
	},
): T[] | undefined => {
	if (!suggestions) {
		return undefined;
	}

	return sortSuggestions(filterSuggestions(suggestions, currentFilter, currentUserId), currentSort);
};
